import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import logo from "../assets/images/logo.svg";
import arrowIcon from "../assets/icons/arrow.svg";
import { getAPIBaseURL, getDefaultHeaders } from "../helpers/api_helper";
import EmptyComponent from "./EmptyComponent";
import PDFViewer from "./PDFViewer";
import RejectedMilkViewer from "./RejectedMilkViewer";
import Switch from "./Switch";
import emptyLogo from "../assets/images/banner.svg";

const getReportDataSource = (config, cityKey, category, product) => {
  const source = config?.[cityKey]?.[category]?.[product]?.reportSource;
  return source || cityKey.split(" ")[0];
};

const getDateInputValue = (date = new Date()) => {
  const dateToFormat = new Date(date);
  
  dateToFormat.setDate(dateToFormat.getDate() - 1);

  const year = dateToFormat.getFullYear();
  const month = String(dateToFormat.getMonth() + 1).padStart(2, "0");
  const day = String(dateToFormat.getDate()).padStart(2, "0");
  
  return `${year}-${month}-${day}`;
};

const LandingPage = () => {
  const [appConfig, setAppConfig] = useState(null);
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [tests, setTests] = useState(null);
  const [error, setError] = useState(null);
  const [availableCities, setAvailableCities] = useState([]);
  const [cityCode, setCityCode] = useState("");
  const [availableCategories, setAvailableCategories] = useState([]);
  const [productCategory, setProductCategory] = useState("");
  const [availableProducts, setAvailableProducts] = useState([]);
  const [productName, setProductName] = useState(null);
  const [batchNumber, setBatchNumber] = useState(null);
  const [batchNumberError, setBatchNumberError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPDF, setShowPDF] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [viewMode, setViewMode] = useState("batchResults");
  const [selectedRejectedDate, setSelectedRejectedDate] = useState(
    getDateInputValue(),
  );
  const [rejectedMilkTests, setRejectedMilkTests] = useState(null);
  const [isRejectedMilkLoading, setIsRejectedMilkLoading] = useState(false);
  const [maxDate, setMaxDate] = useState(null);

  useEffect(()=>{
    const yesterday = getDateInputValue();
    setMaxDate(yesterday);
  },[])

  const updateDependentDropdowns = useCallback(
    (newCity, newCategory) => {
      const categories = Object.keys(appConfig?.[newCity] || {});
      setAvailableCategories(categories);

      const categoryToUse = newCategory || categories[0];
      setProductCategory(categoryToUse);

      const products = Object.keys(appConfig?.[newCity]?.[categoryToUse] || {});
      setAvailableProducts(products);

      setProductName(
        categoryToUse === "Daily Pro+" ? "Protein Milk" : products[0] || null,
      );
    },
    [appConfig],
  );

  useEffect(() => {
    if (appConfig && cityCode) {
      updateDependentDropdowns(cityCode);
    }
  }, [appConfig, cityCode, updateDependentDropdowns]);

  const hideError = useCallback(() => setError(null), []);

  const fetchRejectedMilk = useCallback((date) => {
    setIsRejectedMilkLoading(true);

    const baseUrl = getAPIBaseURL();
    const headers = getDefaultHeaders();
    const url = `${baseUrl}rejected-milk?rejected_on_date=${date}&page=1&size=10`;

    axios
      .get(url, { headers })
      .then((response) => {
        setError(null);
        setRejectedMilkTests(response.data);
      })
      .catch((err) => setError(err))
      .finally(() => setIsRejectedMilkLoading(false));
  }, []);

  const validateBatchNumber = (batchNum) => {
    if (!batchNum) return false;
    const twoCharPattern =
      /^[A-Z0-9]{2}(0[1-9]|[12][0-9]|3[01])(0[1-9]|1[0-2])\d{2}$/;
    const threeCharPattern =
      /^[A-Z]{3}(0[1-9]|[12][0-9]|3[01])(0[1-9]|1[0-2])\d{2}$/;
    return twoCharPattern.test(batchNum) || threeCharPattern.test(batchNum);
  };

  const onSubmit = (e) => {
    e.preventDefault();

    if (productCategory === "Cereals" && !validateBatchNumber(batchNumber)) {
      setBatchNumberError("* Invalid Batch Number");
      return;
    }
    setBatchNumberError(null);
    setIsLoading(true);
    setTests(null);
    setShowPDF(false);
    setPdfUrl(null);

    const baseUrl = getAPIBaseURL();
    const headers = getDefaultHeaders();

    const url = `${baseUrl}quality/reports?size=100&page=1&batch_number__ilike=${batchNumber}`;

    axios
      .get(url, { headers })
      .then((response) => {
        setError(null);
        const testResults = response.data;
        setTests(testResults);

        if (testResults?.data?.length > 0) {
          setPdfUrl(testResults.data[0].pdf_file);
          setShowPDF(true);
        }
      })
      .catch((err) => setError(err))
      .finally(() => {
        setIsLoading(false);
        setHasSearched(true);
      });
  };

  const handleProductChange = (e) => {
    setProductName(e.target.value);
    setTests(null);
  };

  const handleCityCodeChange = (e) => {
    setCityCode(e.target.value);
    setTests(null);
  };

  const handleProductCategoryChange = (e) => {
    updateDependentDropdowns(cityCode, e.target.value);
    setTests(null);
    setBatchNumber(null);
    setBatchNumberError(null);
  };

  const handleBatchNumberChange = (e) => {
    setBatchNumber(e.target.value);
    setTests(null);
    setHasSearched(false);
    setShowPDF(false);
    setPdfUrl(null);
    if (batchNumberError) setBatchNumberError(null);
  };

  const handleClosePDF = () => {
    setShowPDF(false);
    setPdfUrl(null);
  };

  const handleRejectedMilkDateChange = (date) => {
    setSelectedRejectedDate(date);
    fetchRejectedMilk(date);
  };

  const handleViewModeChange = (newViewMode) => {
    setViewMode(newViewMode);
    // Reset all data and input states
    setTests(null);
    setHasSearched(false);
    setShowPDF(false);
    setPdfUrl(null);
    setError(null);
    setIsLoading(false);
    // Reset input values
    setBatchNumber(null);
    setBatchNumberError(null);

    if (newViewMode === "rejectedQuantity") {
      fetchRejectedMilk(selectedRejectedDate);
    }
  };

  const isEmptyState =
    viewMode === "batchResults" &&
    !error &&
    !tests &&
    !isLoading &&
    !isConfigLoading &&
    !hasSearched;

  return (
    <div>
      <div className="header">
        <div className="logo-container">
          <img src={logo} alt="logo" />
        </div>
      </div>

      <div className="landing-page-container">
        <div className="heading">
          <h1 className="lexend-700">Quality Reports</h1>
        </div>

        <div className="filters">
          <div className="filters-top">
            <div className="switch-container">
              <Switch
                options={[
                  { value: "batchResults", label: "Batch Results" },
                  { value: "rejectedQuantity", label: "Rejected Milk" },
                ]}
                defaultValue="batchResults"
                onChange={handleViewModeChange}
              />
            </div>
            {viewMode === "batchResults" ? (
              <div className="sub-heading">
                <div className="description">
                  <p className="lexend-400">
                    Every batch we source is tested for quality and safety. Just
                    enter the batch number on your pack to see the report
                  </p>
                </div>
              </div>
            ) : (
              <div className="sub-heading">
                <div className="description">
                  <p className="lexend-400">
                    If a batch of milk fails even one test, it is rejected and
                    never enters our system. Here's how much milk we rejected to
                    keep your family safe.
                  </p>
                </div>
              </div>
            )}
          </div>

          {viewMode === "batchResults" && (
            <div className="form-container">
              <form onSubmit={onSubmit} className="search-form">
                  <div className="field">
                    <div className="date-field-wrapper">
                      <input
                        type="text"
                        className="date-field"
                        placeholder="Enter batch number"
                        onChange={handleBatchNumberChange}
                        value={batchNumber || ""}
                        required
                        disabled={isLoading || isConfigLoading}
                      />
                    </div>
                    {batchNumberError && (
                      <div className="error-container">
                        <div className="error-message">{batchNumberError}</div>
                        <div className="tooltip-container inline">
                          <div className="info-icon">i</div>
                          <div className="tooltip-content">
                            <p>
                              Ensure you enter the batch number from the back of
                              an individual pack to access the report.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                <div className="button-container">
                  <button
                    className="submit-button pointer"
                    type="submit"
                    disabled={isLoading || isConfigLoading}
                  >
                    {isLoading ? "Loading..." : "Show Test Results"}
                    <img src={arrowIcon} alt="arrow" />
                  </button>
                </div>
              </form>
            </div>
          )}
          {viewMode === "rejectedQuantity" && (
            <RejectedMilkViewer
              data={rejectedMilkTests}
              isLoading={isRejectedMilkLoading}
              selectedDate={selectedRejectedDate}
              onDateChange={handleRejectedMilkDateChange}
              maxDate={maxDate}
            />
          )}
        </div>
      </div>

      <div className="content-container">
        <div className="image-content">
          {error && (
            <div className="toast-message">
              <span className="error-icon">⚠</span>
              Oops! Something went wrong, please try again after sometime.
              <button
                onClick={hideError}
                style={{
                  marginLeft: 8,
                  background: "none",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                ✕
              </button>
            </div>
          )}

          {isConfigLoading && (
            <div className="loading-container">
              <p>Loading configuration...</p>
            </div>
          )}

          {isLoading && !isConfigLoading && (
            <div className="loading-container">
              <p>Loading test results...</p>
            </div>
          )}

          {/* {isEmptyState && <EmptyComponent />} */}

          {hasSearched && tests && (!tests.data || tests.data.length === 0) && (
            <EmptyComponent
              message="Nothing found. Please try a different batch number."
              hasSearched={hasSearched}
            />
          )}
          {!hasSearched && (
            <img className="empty-image" src={emptyLogo} alt="empty-filter" style={{ marginBottom: "20px" }} />
          )}
        </div>
      </div>

      {showPDF && <PDFViewer pdfUrl={pdfUrl} onClose={handleClosePDF} />}
    </div>
  );
};

export default LandingPage;
